import type {
  TEbookDynamicField,
  TEbookEmRange,
  TEbookInfoBoxPreset,
  TEbookMarginField,
  TEbookPageNumberPositionOption,
  TEbookPhotoCropStyle,
  TEbookPhotoFrame,
  TEbookPageShape,
  TEbookPanelLink,
  TEbookSettingsPage,
  TEbookSpecialCharacter,
  TEbookUnit,
  TEbookUnitSpec,
} from '@/types/ebook-editor'
import type { TBoxSides } from '@/types/geometry'
import type { TDropdownOption } from '@/types/dropdown'
import type { TIconLayer } from '@/types/icons'

export const newEbookType = 'new-ebook'

export const classicEbookLabel = 'Classic'

export const ebookDynamicFields: TEbookDynamicField[] = [
  { id: 'author-name', label: 'Author name', token: '[Author name]' },
  { id: 'book-title', label: 'Book title', token: '[Book title]' },
  { id: 'year', label: 'Year', token: '[Year]' },
  { id: 'chapter-title', label: 'Chapter title', token: '[Chapter title]' },
  { id: 'chapter-number', label: 'Chapter number', token: '[Chapter number]' },
]

export const ebookSpecialCharacters: TEbookSpecialCharacter[] = [
  { char: '\u201C', name: 'Left double quote' },
  { char: '\u201D', name: 'Right double quote' },
  { char: '\u2018', name: 'Left single quote' },
  { char: '\u2019', name: 'Right single quote / apostrophe' },
  { char: '\u2014', name: 'Em dash' },
  { char: '\u2013', name: 'En dash' },
  { char: '\u2026', name: 'Ellipsis' },
  { char: '\u2022', name: 'Bullet' },
  { char: '\u2020', name: 'Dagger' },
  { char: '\u2021', name: 'Double dagger' },
  { char: '\u00A7', name: 'Section' },
  { char: '\u00B6', name: 'Pilcrow' },
  { char: '\u00A9', name: 'Copyright' },
  { char: '\u00AE', name: 'Registered' },
  { char: '\u2122', name: 'Trademark' },
  { char: '\u00B0', name: 'Degree' },
  { char: '\u00D7', name: 'Multiplication' },
  { char: '\u00B1', name: 'Plus-minus' },
  { char: '\u00BD', name: 'One half' },
  { char: '\u00BC', name: 'One quarter' },
  { char: '\u00BE', name: 'Three quarters' },
  { char: '\u00AB', name: 'Left guillemet' },
  { char: '\u00BB', name: 'Right guillemet' },
  { char: '\u00BF', name: 'Inverted question' },
]

export const ebookPhotoFrames: TEbookPhotoFrame[] = [
  { id: 'original', label: 'Original', aspect: null },
  { id: '1:1', label: '1:1', aspect: 1 },
  { id: '4:5', label: '4:5', aspect: 4 / 5 },
  { id: '3:2', label: '3:2', aspect: 3 / 2 },
  { id: '16:9', label: '16:9', aspect: 16 / 9 },
]

export const ebookPhotoCropReset: TEbookPhotoCropStyle = { zoom: 1, focusX: 50, focusY: 50 }

export const ebookEdgeHandleClasses: Record<string, string> = {
  w: 'w-2.5 h-6 top-1/2 -translate-y-1/2 left-0 -translate-x-1/2 cursor-w-resize',
  e: 'w-2.5 h-6 top-1/2 -translate-y-1/2 right-0 translate-x-1/2 cursor-e-resize',
  n: 'w-6 h-2.5 left-1/2 -translate-x-1/2 top-0 -translate-y-1/2 cursor-n-resize',
  s: 'w-6 h-2.5 left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 cursor-s-resize',
}

export const ebookMinObjectSize = 12

export const ebookObjectColors = [
  '#14191f',
  '#52637a',
  '#ffffff',
  '#006efe',
  '#cce2ff',
  '#5326bd',
  '#d62929',
  '#29a341',
  '#fca004',
]

export const ebookInfoBoxPresets: TEbookInfoBoxPreset[] = [
  { kind: 'info', label: 'Info', tint: '#eaf2ff', accent: '#2563eb' },
  { kind: 'note', label: 'Note', tint: '#f4f6f8', accent: '#7a8698' },
  { kind: 'warning', label: 'Warning', tint: '#fff8eb', accent: '#b4770e' },
]

export const ebookInfoBoxColors = ['#2563eb', '#7a8698', '#b4770e', '#5326bd', '#d62929', '#29a341']

export const ebookLineHeights: Record<string, number> = {
  chapterTitle: 1.2,
  heading2: 1.3,
  heading3: 1.4,
  heading4: 1.5,
  paragraph: 1.6,
  glossaryTerm: 1.6,
  glossaryDefinition: 1.6,
}

export const ebookLineHeightTypes = ['chapterTitle', 'heading', 'paragraph', 'glossaryTerm', 'glossaryDefinition']

export const ebookInfoBoxPadding: TBoxSides = [12, 18, 12, 18]

export const ebookInfoBoxBorder: TBoxSides = [0, 0, 0, 3]

export const ebookInfoBoxMaxPadding = 60

export const ebookInfoBoxMaxBorder = 20

export const ebookStrokeStyles = [
  { name: 'solid', label: 'Solid' },
  { name: 'dashed', label: 'Dashed' },
  { name: 'dotted', label: 'Dotted' },
]

export const ebookSettingsLinks: TEbookPanelLink<TEbookSettingsPage>[] = [
  { id: 'details', label: 'Book details', summary: 'Title, author, ISBN', mark: 'details' },
  { id: 'page', label: 'Page setup', summary: 'Size, margins, numbers', mark: 'page' },
  { id: 'paragraphs', label: 'Paragraphs', summary: 'Font, spacing and indentation', mark: 'paragraphs' },
]

export const ebookUnits: Record<TEbookUnit, TEbookUnitSpec> = {
  in: { label: 'in', perInch: 1, step: 0.125 },
  mm: { label: 'mm', perInch: 25.4, step: 1 },
}

export const ebookMarginRange = { min: 24, max: 192 }

export const ebookColumnGap = 24

export const ebookColumnGapRange = { min: 0, max: 96 }

export const ebookPageNumberPositions: TEbookPageNumberPositionOption[] = [
  { name: 'top-left', label: 'Top left' },
  { name: 'top-center', label: 'Top center' },
  { name: 'top-right', label: 'Top right' },
  { name: 'bottom-left', label: 'Bottom left' },
  { name: 'bottom-center', label: 'Bottom center' },
  { name: 'bottom-right', label: 'Bottom right' },
]

export const ebookContentsTitle = 'Contents'

export const ebookContentsId = 'contents'

export const ebookColumnOptions = [
  { name: '1', label: 'One' },
  { name: '2', label: 'Two' },
]

export const ebookBelowTextHint = 'Hold Alt (Option on a Mac) and click to select an object below the text.'

export const ebookDefaultFormatId = '215_279'

export const ebookPageShapes: TEbookPageShape[] = ['portrait', 'square', 'landscape']

export const ebookPageFormatNames: Record<string, string> = {
  '127_203': 'Pocket',
  '128_198': 'B-format',
  '139_215': 'Digest',
  '152_228': 'US Trade',
  '155_233': 'Royal',
  '188_246': 'Crown Quarto',
  '210_296': 'A4',
  '215_279': 'Letter',
}

export const ebookLegacyFormatIds: Record<string, string> = {
  letter: '215_279',
  a4: '210_296',
  trade: '152_228',
  digest: '139_215',
  pocket: '127_203',
  a5: '139_215',
}

export const ebookPairedMargins: TEbookMarginField[] = [
  { label: 'Sides', sides: ['left', 'right'] },
  { label: 'Top and bottom', sides: ['top', 'bottom'] },
]

export const ebookSeparateMargins: TEbookMarginField[] = [
  { label: 'Top', icon: 'fa-light fa-border-top', sides: ['top'] },
  { label: 'Bottom', icon: 'fa-light fa-border-bottom', sides: ['bottom'] },
  { label: 'Left', icon: 'fa-light fa-border-left', sides: ['left'] },
  { label: 'Right', icon: 'fa-light fa-border-right', sides: ['right'] },
]

export const ebookParagraphIndent: TEbookEmRange = { min: 0.5, max: 4, step: 0.25 }

export const ebookParagraphSpacing: TEbookEmRange = { min: 0, max: 2, step: 0.25 }

export const ebookSpacedMinimum = 0.25

export const ebookOpeningSkips = ['chapterTitle', 'heading', 'image', 'horizontalRule', 'pageBreak']

export const ebookParagraphStyles = [
  { name: 'spaced', label: 'No indent', icon: 'fa-light fa-align-justify' },
  { name: 'indented', label: 'Indented', icon: 'fa-light fa-indent' },
]

export const ebookPlainOpeningIcon: TIconLayer[] = [
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3 up-6' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 up-6' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3 up-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 up-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3 down-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 down-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3 down-6' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 down-6' },
]

export const ebookDropCapIcon: TIconLayer[] = [
  { icon: 'fa-light fa-a', transform: 'shrink-9 left-4 up-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 up-6' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 up-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3 down-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 down-3' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 left-3 down-6' },
  { icon: 'fa-light fa-minus', transform: 'shrink-8 right-3 down-6' },
]

export const ebookChapterOpenings = [
  { name: 'plain', label: 'Plain', layers: ebookPlainOpeningIcon },
  { name: 'dropCap', label: 'Drop cap', layers: ebookDropCapIcon },
]

export const ebookReadingDirections: TDropdownOption[] = [
  { name: 'ltr', label: 'Left to right', icon: 'fa-arrow-right-from-line' },
  { name: 'rtl', label: 'Right to left', icon: 'fa-arrow-left-from-line' },
]

export const ebookElementStyles = [
  { name: 'solid', label: 'Solid' },
  { name: 'outline', label: 'Outline' },
]

export const ebookIconSize = 96

export const ebookIconColor = '#14191f'

export const ebookOutlineStrokeWidth = 4

export const ebookFontWeightLabels: Record<number, string> = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semi Bold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
}
